import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, Activity, Target, TrendingUp } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface EngagementData {
  dailyActiveUsers: number;
  weeklyActiveUsers: number;
  avgSessionsPerUser: number;
  completionRate: number;
  dauChange: number;
  wauChange: number;
  sessionChange: number;
  completionChange: number;
}

export function EngagementMetrics() {
  const [data, setData] = useState<EngagementData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchEngagementData() {
      try {
        const now = new Date();
        const today = now.toISOString().split('T')[0];
        const yesterday = new Date(now.setDate(now.getDate() - 1)).toISOString().split('T')[0];
        const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        const twoWeeksAgo = new Date(Date.now() - 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

        // Daily Active Users (users with evaluations today)
        const { data: dauData } = await supabase
          .from('simulation_evaluations')
          .select('auth_user_id')
          .gte('created_at', today);
        const dau = new Set(dauData?.map(e => e.auth_user_id)).size;

        // Yesterday's DAU for comparison
        const { data: yesterdayDauData } = await supabase
          .from('simulation_evaluations')
          .select('auth_user_id')
          .gte('created_at', yesterday)
          .lt('created_at', today);
        const yesterdayDau = new Set(yesterdayDauData?.map(e => e.auth_user_id)).size;

        // Weekly Active Users (last 7 days)
        const { data: wauData } = await supabase
          .from('simulation_evaluations')
          .select('auth_user_id')
          .gte('created_at', weekAgo);
        const wau = new Set(wauData?.map(e => e.auth_user_id)).size;

        // Previous week WAU for comparison
        const { data: prevWauData } = await supabase
          .from('simulation_evaluations')
          .select('auth_user_id')
          .gte('created_at', twoWeeksAgo)
          .lt('created_at', weekAgo);
        const prevWau = new Set(prevWauData?.map(e => e.auth_user_id)).size;

        // Average sessions per user (last 30 days)
        const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        const { data: sessionsData } = await supabase
          .from('simulation_evaluations')
          .select('auth_user_id, id')
          .gte('created_at', thirtyDaysAgo);
        
        const uniqueUsers = new Set(sessionsData?.map(e => e.auth_user_id)).size;
        const totalSessions = sessionsData?.length || 0;
        const avgSessions = uniqueUsers > 0 ? totalSessions / uniqueUsers : 0;

        // Previous 30 days for comparison
        const sixtyDaysAgo = new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        const { data: prevSessionsData } = await supabase
          .from('simulation_evaluations')
          .select('auth_user_id, id')
          .gte('created_at', sixtyDaysAgo)
          .lt('created_at', thirtyDaysAgo);
        
        const prevUniqueUsers = new Set(prevSessionsData?.map(e => e.auth_user_id)).size;
        const prevTotalSessions = prevSessionsData?.length || 0;
        const prevAvgSessions = prevUniqueUsers > 0 ? prevTotalSessions / prevUniqueUsers : 0;

        // Completion rate (submitted vs total)
        const { data: completionData } = await supabase
          .from('simulation_evaluations')
          .select('is_submitted')
          .gte('created_at', thirtyDaysAgo);
        
        const submitted = completionData?.filter(e => e.is_submitted).length || 0;
        const total = completionData?.length || 0;
        const completionRate = total > 0 ? (submitted / total) * 100 : 0;

        // Previous completion rate
        const { data: prevCompletionData } = await supabase
          .from('simulation_evaluations')
          .select('is_submitted')
          .gte('created_at', sixtyDaysAgo)
          .lt('created_at', thirtyDaysAgo);
        
        const prevSubmitted = prevCompletionData?.filter(e => e.is_submitted).length || 0;
        const prevTotal = prevCompletionData?.length || 0;
        const prevCompletionRate = prevTotal > 0 ? (prevSubmitted / prevTotal) * 100 : 0;

        setData({
          dailyActiveUsers: dau,
          weeklyActiveUsers: wau,
          avgSessionsPerUser: Math.round(avgSessions * 10) / 10,
          completionRate: Math.round(completionRate),
          dauChange: yesterdayDau > 0 ? Math.round(((dau - yesterdayDau) / yesterdayDau) * 100) : 0,
          wauChange: prevWau > 0 ? Math.round(((wau - prevWau) / prevWau) * 100) : 0,
          sessionChange: prevAvgSessions > 0 ? Math.round(((avgSessions - prevAvgSessions) / prevAvgSessions) * 100) : 0,
          completionChange: prevCompletionRate > 0 ? Math.round(completionRate - prevCompletionRate) : 0,
        });
      } catch (error) {
        console.error('Error fetching engagement data:', error);
      } finally {
        setLoading(false);
      }
    }

    fetchEngagementData();
  }, []);

  const metrics = [
    {
      label: 'Daily Active Users',
      value: data?.dailyActiveUsers || 0,
      change: data?.dauChange || 0,
      icon: Users,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10',
    },
    {
      label: 'Weekly Active Users',
      value: data?.weeklyActiveUsers || 0,
      change: data?.wauChange || 0,
      icon: Activity,
      color: 'text-green-500',
      bgColor: 'bg-green-500/10',
    },
    {
      label: 'Avg Sessions/User',
      value: data?.avgSessionsPerUser || 0,
      change: data?.sessionChange || 0,
      icon: Target,
      color: 'text-purple-500',
      bgColor: 'bg-purple-500/10',
    },
    {
      label: 'Completion Rate',
      value: `${data?.completionRate || 0}%`,
      change: data?.completionChange || 0,
      icon: TrendingUp,
      color: 'text-orange-500',
      bgColor: 'bg-orange-500/10',
      suffix: 'pts',
    },
  ];

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Engagement Metrics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="animate-pulse bg-muted rounded-lg h-24" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Engagement Metrics</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {metrics.map((metric) => {
            const Icon = metric.icon;
            const isPositive = metric.change >= 0;
            return (
              <div 
                key={metric.label}
                className="bg-card border border-border rounded-lg p-4"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className={`p-2 rounded-lg ${metric.bgColor}`}>
                    <Icon className={`h-4 w-4 ${metric.color}`} />
                  </div>
                  <span className={`text-xs font-medium ${isPositive ? 'text-green-500' : 'text-red-500'}`}>
                    {isPositive ? '+' : ''}{metric.change}{metric.suffix || '%'}
                  </span>
                </div>
                <p className="text-2xl font-bold text-foreground">{metric.value}</p>
                <p className="text-xs text-muted-foreground mt-1">{metric.label}</p>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
